/**
 * Axios Instance with Authentication
 *
 * Features:
 * - Automatically attaches JWT access token to requests
 * - Handles 401 errors by refreshing token and retrying
 * - Prevents multiple simultaneous refresh calls
 * - Queues failed requests while refresh is in progress
 * - Blocks requests if user has been denied access
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { getBaseURL } from '../constants/common.constants';
import { tokenService } from '../auth/tokenService';
export const baseURL = getBaseURL();
const axiosInstance = axios.create({
    baseURL,
    withCredentials: true,
});
// Queue for requests waiting for token refresh
let isRefreshing = false;
let failedQueue = [];
// Callback for auth errors (set by setupInterceptors)
let onAuthError = null;
// Track interceptor IDs so we can eject on re-setup
let requestInterceptorId = null;
let responseInterceptorId = null;
/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        }
        else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};
/**
 * Setup axios interceptors for authentication.
 *
 * Identity is carried entirely by the JWT bearer token; the backend derives
 * the user UUID from the token's `sub` claim, so the client no longer needs
 * to supply (or have a fallback for) any user-id header.
 *
 * @param showBackdrop    Function to show loading backdrop
 * @param hideBackdrop    Function to hide loading backdrop
 * @param handleAuthError Callback invoked when auth fails (e.g. redirect to login)
 */
const setupInterceptors = (showBackdrop, hideBackdrop, handleAuthError) => {
    // Store auth error callback
    if (handleAuthError) {
        onAuthError = handleAuthError;
    }
    // Eject previous interceptors if re-initialising
    if (requestInterceptorId !== null) {
        axiosInstance.interceptors.request.eject(requestInterceptorId);
    }
    if (responseInterceptorId !== null) {
        axiosInstance.interceptors.response.eject(responseInterceptorId);
    }
    // Request interceptor - attach token
    requestInterceptorId = axiosInstance.interceptors.request.use((config) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        // Hard-block every request if the user has been denied access.
        // (Auth endpoints are exempt below so the user can still log out.)
        if (sessionStorage.getItem('accessDenied') === 'true') {
            return Promise.reject(new Error('ACCESS_DENIED'));
        }
        // Skip auth for auth endpoints (they handle their own auth)
        if ((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes('/auth/')) {
            return config;
        }
        // Get access token (will refresh if needed)
        try {
            const token = yield tokenService.getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        catch (error) {
            console.error('Failed to get access token:', error);
        }
        // NOTE: We deliberately do NOT attach X-User-Id from the client.
        // The backend middleware (pin_x_user_id_to_jwt) derives the user UUID
        // from the verified JWT `sub` claim and injects it server-side. Sending
        // it from here would be discarded anyway and only widens the spoofing
        // surface for any future route that forgets to depend on the token.
        // Handle backdrop message
        if ((_b = config === null || config === void 0 ? void 0 : config.params) === null || _b === void 0 ? void 0 : _b.forceReload) {
            delete config.params.forceReload;
        }
        let message;
        if (config.headers &&
            Object.prototype.hasOwnProperty.call(config.headers, 'X-Backdrop-Message')) {
            message = String(config.headers['X-Backdrop-Message']);
        }
        if (message && message.trim() !== '') {
            showBackdrop(message);
        }
        return config;
    }), (error) => {
        hideBackdrop();
        return Promise.reject(error);
    });
    // Response interceptor - handle 401 and refresh
    responseInterceptorId = axiosInstance.interceptors.response.use((response) => {
        hideBackdrop();
        return response;
    }, (error) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        hideBackdrop();
        const originalRequest = error.config;
        // If not 401, or already retried, or is an auth endpoint, reject
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) !== 401 ||
            originalRequest._retry ||
            ((_b = originalRequest.url) === null || _b === void 0 ? void 0 : _b.includes('/auth/'))) {
            return Promise.reject(error);
        }
        // Handle 401 - try to refresh token
        if (isRefreshing) {
            // Wait for the ongoing refresh to complete
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then((token) => {
                if (token) {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                }
                return Promise.reject(error);
            })
                .catch((err) => {
                return Promise.reject(err);
            });
        }
        // Start refresh
        originalRequest._retry = true;
        isRefreshing = true;
        try {
            const newToken = yield tokenService.refreshToken();
            if (newToken) {
                processQueue(null, newToken);
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            }
            else {
                processQueue(error, null);
                if (onAuthError) {
                    onAuthError();
                }
                return Promise.reject(error);
            }
        }
        catch (refreshError) {
            processQueue(refreshError, null);
            if (onAuthError) {
                onAuthError();
            }
            return Promise.reject(refreshError);
        }
        finally {
            isRefreshing = false;
        }
    }));
};
export { axiosInstance, setupInterceptors };
